import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { doc, getDoc } from 'firebase/firestore';
import nativeFirestore from '@react-native-firebase/firestore';
import { db } from '../services/firebaseConfig';
import { useAppStore } from '../store/useAppStore';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL VEHÍCULO CON EL QUE DE VERDAD TRABAJA EL CHOFER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── POR QUÉ HACE FALTA, Y POR QUÉ ES ESTE ORDEN ──────────────────────────
 *
 * El vehículo decide DINERO en dos sitios: la cuota mensual que se le cobra y
 * el bono de referidos que se le paga. Así que la pantalla tiene que mirar el
 * mismo que mira el servidor, o promete una cifra y se cobra o se paga otra.
 *
 * El orden importa:
 *   1. `kyc_submissions/{uid}.vehicleTypeVerificado` — el vehículo que una
 *      persona del equipo APROBÓ mirando los papeles. Es el que manda, porque
 *      es el único que alguien comprobó.
 *   2. `users/{uid}.vehicleType` — lo que el chofer declaró. Sirve de respaldo
 *      para los choferes verificados ANTES de que existiera el campo del KYC.
 *   3. `undefined` — y entonces quien llama decide. No se inventa un vehículo:
 *      suponer «carro» es justo el error que esto viene a cerrar.
 *
 * ── POR QUÉ UN HOOK Y NO UNA COPIA MÁS ───────────────────────────────────
 *
 * Esta resolución vivía escrita a mano dentro de `driver-subscription.tsx`.
 * Cuando el barrido del 4-oct-2026 encontró que `profile.tsx` montaba el modal
 * de recompensas con `vehicleType="CAR"` ESCRITO A MANO —y por eso a un
 * motorista se le prometían RD$1,000 cuando el servidor le paga RD$800—, la
 * salida fácil era copiar esas veinte líneas a la segunda pantalla.
 *
 * Copiarlas es exactamente cómo nació el fallo que se está arreglando: dos
 * sitios que dicen lo mismo hasta que uno cambia. Por eso sale a un hook.
 */
export function useVehiculoDelChofer(): string | undefined {
  const uid = useAppStore((s) => s.userFirebaseUid);
  const [verificado, setVerificado] = useState<string | null>(null);
  const [declarado, setDeclarado] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setVerificado(null);
      setDeclarado(null);
      return;
    }
    let vivo = true;

    const limpiar = (v: unknown) => String(v ?? '').trim().toUpperCase() || null;

    (async () => {
      // El del KYC: el que alguien aprobó.
      try {
        if (Platform.OS !== 'web') {
          const snap: any = await nativeFirestore().collection('kyc_submissions').doc(uid).get();
          const data = snap?.exists?.() ? snap.data() : null;
          if (vivo) setVerificado(limpiar(data?.vehicleTypeVerificado));
        } else {
          const snap = await getDoc(doc(db, 'kyc_submissions', uid));
          if (vivo) setVerificado(limpiar(snap.exists() ? snap.data()?.vehicleTypeVerificado : null));
        }
      } catch (e: any) {
        // Sin ruido: un chofer que todavía no mandó papeles no tiene ese
        // documento, y las reglas deniegan la lectura a quien no es su dueño.
        console.warn('[vehiculo] no se pudo leer el verificado:', e?.message ?? e);
        if (vivo) setVerificado(null);
      }

      // El declarado: respaldo para los verificados de antes.
      try {
        if (Platform.OS !== 'web') {
          const snap: any = await nativeFirestore().collection('users').doc(uid).get();
          const data = snap?.exists?.() ? snap.data() : null;
          if (vivo) setDeclarado(limpiar(data?.vehicleType));
        } else {
          const snap = await getDoc(doc(db, 'users', uid));
          if (vivo) setDeclarado(limpiar(snap.exists() ? snap.data()?.vehicleType : null));
        }
      } catch (e: any) {
        console.warn('[vehiculo] no se pudo leer el declarado:', e?.message ?? e);
        if (vivo) setDeclarado(null);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [uid]);

  return verificado || declarado || undefined;
}
